import { WEEKDAYS } from "../../../Settings/DepartmentConfiguration/utils";
import { type DayAmountFlatMaps, type DayAmountRow, syncRowsToFlatMaps } from "./locationConfigData";

/**
 * The staffing target rows (a group of days sharing one target and one required amount) and the edits
 * the popup makes to them. Pure: each edit returns the new rows together with the per-day fields
 * derived from them, ready to merge into the form data.
 */

type DayAmountRowsChange = { dayAmountRows: DayAmountRow[] } & DayAmountFlatMaps;

const withFlatMaps = (dayAmountRows: DayAmountRow[]): DayAmountRowsChange => ({ dayAmountRows, ...syncRowsToFlatMaps(dayAmountRows) });

/** A new row has no days yet (the admin picks them) and a target and required amount of 1. */
export const addDayAmountRow = (rows: DayAmountRow[], id: string): DayAmountRowsChange =>
	withFlatMaps([...rows, { id, days: [], amount: 1, amountRequired: 1 }]);

export const removeDayAmountRow = (rows: DayAmountRow[], rowId: string): DayAmountRowsChange => withFlatMaps(rows.filter((r) => r.id !== rowId));

/**
 * Days are kept in week order and both amounts at 0 or more. Required is lowered to the target only
 * when the target itself changes: raising required above the target is left for the Save check
 * ("required over target") to report.
 */
export const updateDayAmountRow = (rows: DayAmountRow[], rowId: string, updates: Partial<DayAmountRow>): DayAmountRowsChange =>
	withFlatMaps(
		rows.map((row) => {
			if (row.id !== rowId) return row;
			const updated = { ...row, ...updates };
			// Sort days in WEEKDAYS order
			if (updates.days) {
				updated.days = WEEKDAYS.filter((d) => updated.days.includes(d));
			}
			// Clamp values to non-negative
			updated.amount = Math.max(0, updated.amount);
			updated.amountRequired = Math.max(0, updated.amountRequired);
			// Clamp amountRequired when amount changes
			if (updates.amount !== undefined) {
				updated.amountRequired = Math.min(updated.amountRequired, updates.amount);
			}
			return updated;
		}),
	);

/** The days each row can't pick, keyed by row id: a day belongs to one row only. */
export const disabledDaysByRow = (rows: DayAmountRow[]): Record<string, string[]> => {
	const result: Record<string, string[]> = {};
	for (const row of rows) {
		const otherDays = rows.filter((r) => r.id !== row.id).flatMap((r) => r.days);
		result[row.id] = otherDays;
	}
	return result;
};

/** Whether each row, keyed by row id, runs on a day existing shifts occupy (`occupiedDays` are 0–6, Sun–Sat). */
export const hasOccupiedDaysByRow = (rows: DayAmountRow[], occupiedDays: number[]): Record<string, boolean> => {
	const occupiedShortDays = new Set<string>(occupiedDays.map((idx) => WEEKDAYS[idx]).filter(Boolean));
	const result: Record<string, boolean> = {};
	for (const row of rows) {
		result[row.id] = row.days.some((day) => occupiedShortDays.has(day));
	}
	return result;
};
